import { about, offices } from "@/content/site";
import usePageMeta from "@/hooks/usePageMeta";
import { PageHero } from "@/components/sections/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { SectionHeader } from "@/components/common/SectionHeader";
import { Rich } from "@/components/common/Rich";
import { Reveal } from "@/components/motion/Reveal";
import { MapPin } from "lucide-react";
import { OfficePartner } from "@/components/common/OfficePartner";

export default function About() {
  usePageMeta(about.meta);
  return (
    <>
      <PageHero hero={about.hero} testId="about-hero" />

      <section className="section-y border-t border-line" data-testid="about-story">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <p className="eyebrow lg:col-span-3">Our story</p>
          <div className="space-y-8 lg:col-span-9">
            {about.story.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="font-display text-2xl font-medium leading-[1.35] tracking-[-0.025em] text-ink sm:text-3xl lg:text-[2rem]">
                  <Rich text={p} />
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="about-approach">
        <div className="container-x">
          <SectionHeader eyebrow="Approach" title="How SPC works with partners." className="mb-14" />
          <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {about.approach.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08} className="border-t border-ink/80 pt-6" data-testid={`about-approach-${i}`}>
                <h3 className="font-display text-2xl font-semibold tracking-[-0.03em] text-ink">{a.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-muted">{a.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="about-network">
        <div className="container-x">
          <SectionHeader eyebrow="Network" title="An ecosystem, not a contact list." className="mb-14" />
          <div className="grid overflow-hidden border border-line bg-surface sm:grid-cols-2 lg:grid-cols-3">
            {about.network.map((n, i) => (
              <Reveal key={n.title} delay={i * 0.05} className="border-b border-line bg-surface p-8 sm:border-r" data-testid={`network-${i}`}>
                <span className="font-mono text-[13px] text-accent">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-[-0.025em] text-ink">{n.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-muted">{n.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y border-t border-line" data-testid="about-offices">
        <div className="container-x">
          <SectionHeader eyebrow="Offices" title="India, Switzerland and South Korea." className="mb-14" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {offices.map((o, i) => (
              <Reveal key={o.id} delay={i * 0.1} className="spec-corners border border-line bg-surface p-8 sm:p-10" data-testid={`about-office-${o.id}`}>
                <MapPin className="h-6 w-6 text-accent" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-3xl font-semibold tracking-[-0.035em] text-ink">{o.country}</h3>
                <p className="mt-1 text-[14px] text-accent">{o.role}</p>
                <p className="mt-4 text-[15px] text-muted" data-testid={`about-office-city-${o.id}`}>{o.address || o.city}</p>
                <OfficePartner office={o} className="mt-3 text-[15px] text-muted" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
